import type moment from 'moment';
import { trim, sortBy, countBy, size, groupBy } from 'lodash-es';
import type { Day, Event } from '../types/events';
import type { CoreTypes } from '../../../core';
import getDate from '../../../common/get-date';
import type { Options } from '../types';
import { makeChainable } from '../../../common/make-chainable';

const days: Day[] = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

function _parseWeeks (
  weeks:  string | undefined,
  set:    Set<number>,
  method: ('add' | 'delete') = 'add'
): void {
  if (!weeks) return;
  const intervals = weeks.split(',').map(x => trim(x)) as (`${number}-${number}` | `${number}${number}`)[];
  intervals.forEach(interval => {
    if (!interval.includes('-')) {
      set[method](Number(interval));
      return;
    }
    const [start, end] = interval.split('-').map(Number);
    Array.from({ length: end - start + 1 }, (_, index) => start + index)
      .forEach(x => set[method](x));
  });
}


export default (
  input:    Event[],
  options?: Options
): Partial<CoreTypes.SerializedWithOptionalId.Schedule> => {
  const teachers  = new Set<string>();
  const groups    = new Set<string>();
  const locations = new Set<string>();

  const events = input
    .map(event => {
      if (!event.length) return;

      const weeks = new Set<number>();
      const {
        inweek,
        exweek,
        realweeks
      } = event;

      try {
        _parseWeeks(inweek,    weeks, 'add');
        _parseWeeks(realweeks, weeks, 'add');

        if (weeks.size == 0)
          Array.from({ length: 51 }, (_, index) => index + 1).forEach(x => weeks.add(x));
        _parseWeeks(exweek, weeks, 'delete');
      } catch (err) {
        console.error(err);
      }

      if (options?.interval) {
        const { start, end } = options.interval;

        if (start > 52 || start <= 0)
          throw new Error(`(SchoolSoft::From::Schedules) Interval::start is invalid. value ${ start }`);

        if (end > 52 || end <= 0)
          throw new Error(`(SchoolSoft::From::Schedules) Interval::end is invalid. value ${ end }`);

        if (start > end) {
          if (!Array.from(weeks).some(week => (week >= start && week <= 52) || week <= end))
            return;
        } else {
          if (!Array.from(weeks).some(week => week >= start && week <= end))
            return;
        }
      }

      let start: moment.Moment | undefined;
      let end:   moment.Moment | undefined;

      const {
        starttime,
        subject,
        day,
        length,
        teacher,
        group,
        room
      } = event;

      if (day && starttime) {
        const _day: number = days.indexOf(day);

        if (_day > 4) return;

        start = getDate(_day, `${ starttime.substring(0, 2) }:${ starttime.substring(2, 4) }`);
        end   = start.clone().add(length, 'm');
      }

      let _teachers:  string[] | undefined;
      let _groups:    string[] | undefined;
      let _locations: string[] | undefined;

      if (teacher) {
        _teachers = teacher.split(',').map(x => x.trim());
        _teachers?.forEach(x => teachers.add(x));
      }

      if (group) {
        _groups = group.split(',').map(x => x.trim());
        _groups?.forEach(x => groups.add(x));
      }

      if (room) {
        _locations = sortBy(room.split(',').map(x => x.trim()));
        _locations?.forEach(x => locations.add(x));
      }

      const doc: CoreTypes.SerializedWithOptionalId.Event & { subject?: string } ={
        ids:               event.id,
        preferredDuration: event.length,
        weeks:             sortBy(Array.from(weeks)),
        ...subject         && { subject },
        ...start           && { start },
        ...end             && { end },
        ...event.group     && { groups:   _groups  ?.map(to => ({ to })) },
        ...event.teacher   && { teachers: _teachers?.map(to => ({ to })) },
        ...event.room      && {
          inLocations: _locations,
          locations:   _locations?.map((_location, index: number) => (
            {
              locations:  [_location],
              groupIndex: index
            })
          )
        }
      };

      return doc;
    })
    .filter(x => x != null);

  // construct courses from events with same subject, groups and teachers
  const courses = makeChainable(events)
    .chain(
      x => x.filter((e): e is typeof e & { ids: string } => !!e.ids),
      x => groupBy(x, e =>
        e.subject
        + '.g.' + e.groups?.map(x => x.to).join(',')
        // Subject Lunch only have teacher and no groups
        + (e.groups?.length ? '' :  ('.t.' + e.teachers?.map(x => x.to).join(',') ))
      ),
      x => Object.values(x)
        .map(events => {
          // promote shared properties to course
          const { subject, groups, teachers } = events[0];
          for (const e of events) {
            delete e.subject;
            delete e.groups;
          }

          const course: CoreTypes.SerializedWithOptionalId.Course = {
            displayName: `${ subject }-${ (groups ?? teachers)?.map(x => x.to).join(' ') }`,
            events:      events.map(e => e.ids),
            ...groups,
          };

          // try to also promote teachers and locations
          if (size(countBy(events, e => e.teachers?.map(x => x.to).sort().join(','))) == 1) {
            course.teachers = events[0].teachers ?? undefined;
            events.forEach(e => delete e.teachers);
          }
          if (size(countBy(events, e => e.locations?.map(x => x.locations[0]).sort().join(','))) == 1) {
            course.locations = events[0].locations ?? undefined;
            events.forEach(e => delete e.locations);
          }

          return course;
        })
    )
    .value;

  return {
    courses:   courses,
    teachers:  Array.from(teachers) .map(ids => ({ ids, displayName: ids })),
    groups:    Array.from(groups)   .map(ids => ({ ids, displayName: ids })),
    locations: Array.from(locations).map(ids => ({ ids, displayName: ids }))
  };
};